/* eslint-disable no-magic-numbers */
/* eslint-disable no-case-declarations */
/* eslint-disable complexity */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { endOfDay, endOfMonth, endOfQuarter, endOfWeek, startOfMonth, startOfQuarter, startOfWeek } from 'date-fns';
import Button from 'carbon-react/lib/components/button';

import { Forecast, UnpaidInvoice } from '@futrli-sdk/predict';
import { FormattedMessage, useIntl } from '@futrli/localisation';

import * as styles from './invoices.scss';
import { PredictedCashGrid } from '../../performance/cash-flow-performance/predicted-cash-flow-grid';
import { fetchUnpaidInvoices, updateUnpaidInvoiceById } from '../../../../core/api/invoices';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { useTableFilters } from '../../../../utils/table-filters/hooks/use-table-filters/use-table-filters';
import { initialFilterTableState, translatedOptions } from './const';
import { Filter } from '../../../../utils/table-filters/components/filter';
import {
  Data,
  FILTER_TYPE,
  TableFiltersData,
  TABLE_COLUMN,
  SubmitFilterArgs
} from '../../../../utils/table-filters/filter-table-types';
import { InvoiceSummaryOption, NormalizedUnpaidInvoice, translatedInvoicesSummaryOptions } from './types';
import { VirtualizedList, VIRTUALIZED_LIST_TYPE } from '../../../../ui/virtualized-list/virtualized-list';
import { penceToAmount } from '../../../../core/formatters';
import { SidePanelContext } from '../../context/side-panel';
import { ASSISTANT_TAB } from '../../../../ui/assistant-side-panel/assistant-side-panel';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';
import { ForecastingNavigationTabs } from '../navigation/forecasting-navigation-tabs';
import { InvoicesSummary } from './invoices-summary';
import { useAnimationLatency, useOnboardingUrlInfo } from '../../../../forecast/onboarding/utils/hooks';
import { LS_LIVE_FORECAST_ID, SCENARIO_WITH_INVOICES_ONBOARDING_STEP } from '../../../../forecast/onboarding/const';
import bulkUpdateArrow from './bulk-update-arrow.png';
import { ForecastingTitleHeader } from '../forecasting-title-header';
import classNames from 'classnames';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import Tooltip from 'carbon-react/lib/components/tooltip';

const LOCAL_STORAGE_KEY = 'invoices-bills-filters';

const MIN_QUERY_LENGTH = 3;

export const invoicesAndBillsColumns = [
  {
    display_name: <FormattedMessage id="predict.type_uc" defaultMessage="TYPE" />,
    name: TABLE_COLUMN.TYPE,
    classes: clsx(styles.flex1)
  },
  {
    display_name: <FormattedMessage id="predict.contact_uc" defaultMessage="CONTACT" />,
    name: TABLE_COLUMN.CONTACT_NAME,
    classes: clsx(styles.flex2)
  },
  {
    display_name: <FormattedMessage id="predict.amount_uc" defaultMessage="AMOUNT" />,
    name: TABLE_COLUMN.CONVERTED_AMOUNT_TOTAL,
    classes: clsx(styles.flex1)
  },
  {
    display_name: <FormattedMessage id="predict.amount_due_uc" defaultMessage="AMOUNT DUE" />,
    name: TABLE_COLUMN.CONVERTED_AMOUNT_DUE,
    classes: clsx(styles.flex1)
  },
  {
    display_name: <FormattedMessage id="predict.adds_to_reduces_cf_uc" defaultMessage="ADDS TO/REDUCES CASH FLOW" />,
    name: TABLE_COLUMN.EXPECTED_DATE,
    classes: clsx(styles.flex4),
    iconName: 'help-circle',
    showArrowColumn: true,
    tooltipTitle: (
      <div>
        <div className={clsx(styles.tableBoldBodyFont, styles.whiteFont)}>
          <FormattedMessage
            id="predict.payment_days_calculation"
            defaultMessage="Payment days calculation - using the most pessimistic of:"
          />
        </div>
        <ul className={styles.descriptionList}>
          <li>
            <FormattedMessage
              id="predict.predicted_payment_days"
              defaultMessage="Predicted payment days aggregating the last 90 days of payments for this contact."
            />
          </li>
          <li>
            <FormattedMessage
              id="predict.expected_payment_date"
              defaultMessage="If not possible, then expected payment date from the accounting package."
            />
          </li>
          <li>
            <FormattedMessage
              id="predict.due_date_of_invoice"
              defaultMessage="If not present, then the due date of the invoice or bill."
            />
          </li>
        </ul>
      </div>
    )
  },
  {
    display_name: <FormattedMessage id="predict.reference_uc" defaultMessage="REFERENCE" />,
    name: TABLE_COLUMN.INVOICE_NUMBER,
    classes: clsx(styles.flex2)
  },
  {
    display_name: <FormattedMessage id="predict.issued_on_uc" defaultMessage="ISSUED ON" />,
    name: TABLE_COLUMN.ISSUE_DATE,
    classes: clsx(styles.flex1)
  },
  {
    display_name: <FormattedMessage id="predict.due_date_uc" defaultMessage="DUE DATE" />,
    name: TABLE_COLUMN.DUE_DATE,
    classes: clsx(styles.flex1)
  }
];

export const Invoices = () => {
  const intl = useIntl();
  const [data, setData] = useState<NormalizedUnpaidInvoice[] | null>(null);
  const [expectedDateInvoicesSummary, setExpectedDateInvoicesSummary] = useState(InvoiceSummaryOption.Today);
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId: forecastIdParam } = useParams();
  const organisationId = tempOrganisationId || '';
  const workspaceId = tempWorkspaceId || '';

  const { fetchForecastReport, forecast, isConsolidation, isReadOnly } = usePredictionsInfoContext();

  const {
    onboardingStep,
    isOnboardingScenarioWithInvoicesRoute,
    isOnboardingRoute,
    editInvoicesBills
  } = useOnboardingUrlInfo();
  const forecastId = forecastIdParam || forecast?.id;

  const { openSidePanel } = useContext(SidePanelContext);
  const { addErrorMessage } = useContext(PredictManagementContext);

  const {
    bulkUpdatesInvoicesBills: { triggerInvoicesBillsRefetch }
  } = useContext(UserGridSettingsContext);

  const invoicesBillsFiltersString = localStorage.getItem(LOCAL_STORAGE_KEY);

  const invoicesSummaryOptions = useMemo(() => translatedInvoicesSummaryOptions(intl), [intl]);

  const invoicesBillsFiltersObj = useMemo(
    () => (invoicesBillsFiltersString ? (JSON.parse(invoicesBillsFiltersString) as TableFiltersData) : null),
    [invoicesBillsFiltersString]
  );

  const {
    state: filterTableState,
    setInitialData,
    setInitialFiltersAndSort,
    handleCloseFilter,
    handleOpenFilter,
    handleClearColumnFilter,
    handleClearFilters,
    handleSortClick,
    handleSubmit,
    handleSubmitExpectedDateSummary
  } = useTableFilters(initialFilterTableState);

  const { filterAnchorElement, activeColumn, sortByColumn, filters, sort, displayedData } = filterTableState;

  const fetchInvoiceBillsData = useCallback(async () => {
    try {
      const liveForecastIdLocalStorage = localStorage.getItem(LS_LIVE_FORECAST_ID);
      let forecastIdToUse;
      if (isOnboardingScenarioWithInvoicesRoute && liveForecastIdLocalStorage) {
        forecastIdToUse = liveForecastIdLocalStorage;
      } else {
        forecastIdToUse = forecastId as string;
      }
      const unpaidInvoicesBills = await fetchUnpaidInvoices(workspaceId!, organisationId, forecastIdToUse);
      const normalizedInvoicesBills = unpaidInvoicesBills
        .map(unpaid => ({
          ...unpaid,
          due_date: new Date(unpaid.due_date),
          issue_date: new Date(unpaid.issue_date),
          converted_amount_due: penceToAmount(Number(unpaid.amount_due)),
          converted_amount_total: penceToAmount(Number(unpaid.amount_total)),
          expected_date: new Date(unpaid.next_payment_info.date)
        }))
        .sort((a, b) => {
          return a.due_date.getTime() - b.due_date.getTime();
        });

      return normalizedInvoicesBills;
    } catch {
      addErrorMessage?.(new Error('Fail to fetch invoices and bills'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastId, organisationId, workspaceId]);

  const showAnimatedArrow = useAnimationLatency(
    isOnboardingScenarioWithInvoicesRoute &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED &&
      editInvoicesBills === 'true' &&
      !!data
  );

  useEffect(() => {
    if (!forecastId) {
      return;
    }

    const fetchCashFlowPerformanceData = async () => {
      const invoicesBillsDataWithType = await fetchInvoiceBillsData();
      if (invoicesBillsDataWithType) {
        setData(invoicesBillsDataWithType);
      }
    };

    fetchCashFlowPerformanceData();

    return () => {
      handleClearFilters(initialFilterTableState.filters);
    };
  }, [forecastId, workspaceId, organisationId, fetchInvoiceBillsData, handleClearFilters]);

  // Setting initial values according to filters from local storage
  useEffect(() => {
    if (data) {
      setInitialData((data as unknown) as Data[]);
    }

    if (
      invoicesBillsFiltersObj &&
      invoicesBillsFiltersObj[organisationId] &&
      Object.keys(invoicesBillsFiltersObj[organisationId]).length > 0
    ) {
      // Due date info
      const newDueDateFromDate = invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.DUE_DATE]?.fromDate;
      const newDueDateToDate = invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.DUE_DATE]?.toDate;
      // Issue date info
      const newIssueDateFromDate = invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.ISSUE_DATE]?.fromDate;
      const newIssueDateToDate = invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.ISSUE_DATE]?.toDate;
      // Expected date info
      const newExpectedDateFromDate =
        invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.EXPECTED_DATE]?.fromDate;
      const newExpectedDateToDate = invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.EXPECTED_DATE]?.toDate;
      const newExpectedDateFilterLabel =
        invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.EXPECTED_DATE]?.label;

      const newFilters = {
        ...invoicesBillsFiltersObj[organisationId].filters,
        [TABLE_COLUMN.DUE_DATE]: {
          ...invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.DUE_DATE],
          fromDate: newDueDateFromDate ? new Date(newDueDateFromDate) : undefined,
          toDate: newDueDateToDate ? new Date(newDueDateToDate) : undefined
        },
        [TABLE_COLUMN.ISSUE_DATE]: {
          ...invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.ISSUE_DATE],
          fromDate: newIssueDateFromDate ? new Date(newIssueDateFromDate) : undefined,
          toDate: newIssueDateToDate ? new Date(newIssueDateToDate) : undefined
        },
        [TABLE_COLUMN.EXPECTED_DATE]: {
          ...invoicesBillsFiltersObj[organisationId].filters[TABLE_COLUMN.EXPECTED_DATE],
          fromDate: newExpectedDateFromDate ? new Date(newExpectedDateFromDate) : undefined,
          toDate: newExpectedDateToDate ? new Date(newExpectedDateToDate) : undefined,
          label: newExpectedDateFilterLabel
        }
      };

      if (!newFilters[TABLE_COLUMN.CONVERTED_AMOUNT_TOTAL]) {
        newFilters[TABLE_COLUMN.CONVERTED_AMOUNT_TOTAL] = { filterType: FILTER_TYPE.NUMBER };
      }

      if (newExpectedDateFilterLabel) {
        const isPossibleOption = invoicesSummaryOptions.findIndex(e => e.value === newExpectedDateFilterLabel) !== -1;
        setExpectedDateInvoicesSummary(
          isPossibleOption ? (newExpectedDateFilterLabel as InvoiceSummaryOption) : InvoiceSummaryOption.All
        );
      } else {
        setExpectedDateInvoicesSummary(InvoiceSummaryOption.All);
      }
      const newSort = invoicesBillsFiltersObj[organisationId].sort.sort;
      const newSortByColumn = invoicesBillsFiltersObj[organisationId].sort.sortByColumn;
      setInitialFiltersAndSort(newFilters, newSort, newSortByColumn);
    }

    return () => {
      handleClearFilters(initialFilterTableState.filters);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, setInitialData, organisationId, handleClearFilters, setInitialFiltersAndSort]);

  // When filter is updated, update stored filter values in local storage
  useEffect(() => {
    if (!invoicesBillsFiltersObj) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify({ [organisationId]: { filters, sort: { sortByColumn, sort } } })
      );
    }

    if (invoicesBillsFiltersObj) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify({
          ...invoicesBillsFiltersObj,
          [organisationId]: { filters, sort: { sortByColumn, sort } }
        })
      );
    }
  }, [filters, invoicesBillsFiltersObj, organisationId, sortByColumn, sort]);

  const revalidateData = useCallback(async () => {
    try {
      const [invoicesBillsDataWithType] = await Promise.all([fetchInvoiceBillsData(), fetchForecastReport?.('all')]);
      if (invoicesBillsDataWithType) {
        setData(invoicesBillsDataWithType);
      }
    } catch {
      addErrorMessage?.(new Error('Failed to update row'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchInvoiceBillsData]);

  useEffect(() => {
    if (triggerInvoicesBillsRefetch > 0) {
      revalidateData();
    }
  }, [triggerInvoicesBillsRefetch, revalidateData]);

  const onClearColumnFilter = useCallback(() => {
    handleClearColumnFilter(initialFilterTableState.filters);
    if (filterTableState.activeColumn === TABLE_COLUMN.EXPECTED_DATE) {
      setExpectedDateInvoicesSummary(InvoiceSummaryOption.All);
    }
  }, [handleClearColumnFilter, filterTableState.activeColumn]);

  const updateBillOrInvoice = useCallback(
    async (data: Partial<UnpaidInvoice>, billsORInvoiceId: string): Promise<void> => {
      if (isReadOnly) {
        return;
      }

      if (!workspaceId || !organisationId) {
        throw new Error('Missing workspaceId or OrganisationId for updating the invoice or bill');
      }

      let forecastIdToUse;
      const liveForecastIdLocalStorage = localStorage.getItem(LS_LIVE_FORECAST_ID);
      if (isOnboardingScenarioWithInvoicesRoute && liveForecastIdLocalStorage) {
        forecastIdToUse = liveForecastIdLocalStorage;
      } else {
        forecastIdToUse = forecastId as string;
      }

      try {
        await updateUnpaidInvoiceById(data, workspaceId!, organisationId, forecastIdToUse, billsORInvoiceId);

        const [invoicesBillsDataWithType] = await Promise.all([fetchInvoiceBillsData(), fetchForecastReport?.('all')]);

        if (invoicesBillsDataWithType) {
          setData(invoicesBillsDataWithType);
        }
      } catch {
        addErrorMessage?.(new Error('Failed to update row'));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchInvoiceBillsData, forecastId, isReadOnly, organisationId, workspaceId]
  );

  const handleBulkUpdateClick = () => {
    openSidePanel?.(undefined, ASSISTANT_TAB.BULK_UPDATE_INVOICES_BILLS);
  };

  const handleExpectedDateSummaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      let expectedFromDate;
      let expectedEndDate;
      const today = new Date();
      switch (value) {
        case InvoiceSummaryOption.Today:
          expectedFromDate = today;
          expectedEndDate = endOfDay(today);
          break;
        case InvoiceSummaryOption.ThisWeek:
          expectedFromDate = startOfWeek(today, { weekStartsOn: 1 });
          expectedEndDate = endOfWeek(today, { weekStartsOn: 1 });
          break;
        case InvoiceSummaryOption.ThisMonth:
          expectedFromDate = startOfMonth(today);
          expectedEndDate = endOfMonth(today);
          break;
        case InvoiceSummaryOption.ThisQuarter:
          expectedFromDate = startOfQuarter(today);
          expectedEndDate = endOfQuarter(today);
          break;
        default:
          break;
      }

      handleSubmitExpectedDateSummary(expectedFromDate, expectedEndDate, value);
      setExpectedDateInvoicesSummary(event.target.value as InvoiceSummaryOption);
    },
    [handleSubmitExpectedDateSummary]
  );

  const handleSubmitFilters = useCallback(
    (args: SubmitFilterArgs) => {
      handleSubmit(args);
    },
    [handleSubmit]
  );

  const showPlaceholderForBulkUpdate =
    onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED &&
    !editInvoicesBills &&
    isOnboardingScenarioWithInvoicesRoute;

  const disabledBulkUpdate =
    onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED &&
    editInvoicesBills === 'true' &&
    isOnboardingScenarioWithInvoicesRoute;

  const showBulkUpdateArrow =
    isOnboardingScenarioWithInvoicesRoute &&
    onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED &&
    editInvoicesBills === 'true';

  const showForecastingTabs =
    !isOnboardingRoute ||
    (isOnboardingScenarioWithInvoicesRoute &&
      Number(onboardingStep) > Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED));

  if (
    (forecast?.kind === 'budget' && !isOnboardingRoute) ||
    (forecast?.kind === 'scenario' && !(forecast as Forecast).include_unpaid_invoices && !isOnboardingRoute) ||
    isConsolidation
  ) {
    return null;
  }

  return (
    <div>
      {!isOnboardingRoute && (
        <h2 className={clsx('uiTypeHeaderFont20', styles.noMarginBottom, styles.newSectionTitle)}>
          <ForecastingTitleHeader />
        </h2>
      )}
      {showForecastingTabs && <ForecastingNavigationTabs />}
      {!isOnboardingRoute && (
        <div
          className={clsx(
            styles.cashFlowContainer,
            styles.isSticky,
            styles.isStickyNewForecastDesignsTopNewHeader,
            styles.paddingTop10
          )}
        >
          <PredictedCashGrid hideTitle />
        </div>
      )}
      <InvoicesSummary
        onDueDateSummaryChange={handleExpectedDateSummaryChange}
        dueDateValue={expectedDateInvoicesSummary}
        data={displayedData}
      />
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14, marginTop: 24 }}>
        {!isOnboardingRoute && (
          <h1 className="uiTypeHeaderFont20">
            <FormattedMessage id="predict.invoices_and_bills" defaultMessage="Invoices & bills" />
          </h1>
        )}
        {!isReadOnly && (
          <div style={{ marginLeft: 'auto' }}>
            {showPlaceholderForBulkUpdate ? (
              <div className={styles.bulkUpdatePlaceholder} />
            ) : (
              <div style={{ position: 'relative' }}>
                {showBulkUpdateArrow ? (
                  <>
                    <img
                      alt="onboarding-arrow"
                      src={bulkUpdateArrow}
                      style={{ position: 'absolute', zIndex: 20, top: -134, right: 32 }}
                      className={classNames(styles.fadeInContainer, {
                        [styles.fadeIn]: showAnimatedArrow
                      })}
                    />
                    <Tooltip
                      message={
                        <FormattedMessage
                          id="predict.invoices.button_enabled_after_guided_flow"
                          defaultMessage="This button will be enabled after this guided flow"
                        />
                      }
                    >
                      <Button
                        buttonType="secondary"
                        marginLeft="auto"
                        onClick={handleBulkUpdateClick}
                        disabled={disabledBulkUpdate}
                        size="small"
                      >
                        <FormattedMessage id="common.bulk_update" defaultMessage="Bulk update" />
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <Button
                    buttonType="secondary"
                    marginLeft="auto"
                    onClick={handleBulkUpdateClick}
                    disabled={disabledBulkUpdate}
                    size="small"
                  >
                    <FormattedMessage id="common.bulk_update" defaultMessage="Bulk update" />
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <VirtualizedList
        data={displayedData}
        handleTableFilterClick={handleOpenFilter}
        filters={filters}
        sortByColumn={sortByColumn}
        updateRow={updateBillOrInvoice}
        type={VIRTUALIZED_LIST_TYPE.INVOICES_BILLS}
        columns={invoicesAndBillsColumns}
        revalidateData={revalidateData}
        readOnly={isReadOnly}
      />
      {!displayedData ||
        (!displayedData.length && (
          <div className={styles.noUnpaidSection} data-testid="noUnpaidInvoices">
            <h2 className="uiTypeNotificationTitleMedium">
              {/* {forecast?.kind === 'scenario' ? (
                <FormattedMessage
                  id="predict.invoices_bills.no_unpaid_scenario"
                  defaultMessage="Invoices and bills are not relevant to scenario planning, so we have removed them for you"
                />
              ) : (
                <FormattedMessage
                  id="predict.invoices_bills.no_unpaid"
                  defaultMessage="You don’t have any unpaid invoices and bills"
                />
              )} */}
              <FormattedMessage
                id="predict.you_dont_have_unpaid_invoices"
                defaultMessage="You don’t have any unpaid invoices and bills"
              />
            </h2>
            <p className="uiTypePopupText">
              {/* {forecast?.kind === 'scenario' ? (
                <FormattedMessage
                  id="predict.invoices.no_unpaid.will_appear_when_you_do_scenario"
                  defaultMessage="Don't worry, they will still be there for other purposes"
                />
              ) : (
                <FormattedMessage
                  id={`predict.invoices.no_unpaid.will_appear_when_you_do`}
                  defaultMessage="They’ll appear here when you do"
                />
              )} */}
              <FormattedMessage id="predict.will_appear_when_you_do" defaultMessage="They’ll appear here when you do" />
            </p>
          </div>
        ))}
      <Filter
        data={(data as unknown) as Data[]}
        anchorElement={filterAnchorElement}
        handleSubmit={handleSubmitFilters}
        handleClose={handleCloseFilter}
        handleClearColumnFilter={onClearColumnFilter}
        activeColumn={activeColumn}
        sortByColumn={sortByColumn}
        filters={filters}
        handleSortClick={handleSortClick}
        sort={sort}
        options={translatedOptions(intl)}
        minQueryLength={MIN_QUERY_LENGTH}
      />
    </div>
  );
};
